import { FC, MouseEvent, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Navigate, useParams } from 'react-router-dom';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { RAW_PROD_TYPE_CODE } from '../../../constants/RawProds';
import { useAddRawProd, useRawProdSourceById } from '../../../services/rawProd/rawProd.service';
import { SubAssembly } from '../../../services/rawProd/rawProd.types';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import AddRawProductOverrides from '../AddRawProductOverrides/AddRawProductOverrides';
import RawProdForm from '../RawProdForm/RawProdForm';
import { RawProdFlows, RawProdFormErrors, RawProdFormValues } from '../RawProdForm/RawProdForm.types';

const AddRawProd: FC = () => {
  const queryClient = useQueryClient();
  const navigateWithToast = useNavigateWithToast();
  const { id } = useParams();
  const isAddNew = id === undefined;

  const { data } = useRawProdSourceById(id as string, id !== undefined);
  const rawProdData = data?.data;

  const {
    mutate,
    error: addRawProdError,
    isError: addRawProdIsError,
    isLoading,
  } = useAddRawProd(rawProdData?.mxRef === 'NATION');

  useEffect(() => {
    if (rawProdData?.mxRef === 'NATION') {
      setFormValues({
        id: rawProdData.id,
        mxRef: '',
        moaRawProdNo: rawProdData.moaRawProdNo,
        rawProdNo: rawProdData.rawProdNo,
        rawProdTpCode: rawProdData.rawProdTpCode,
        description: rawProdData.description,
        shortDescription: rawProdData.shortDescription,
        statusNo: rawProdData.statusNo,
        classNo: rawProdData.classNo,
        printSeq: rawProdData.printSeq,
        pcaNo: rawProdData.pcaNo,
        occasionNo: rawProdData.occasionNo,
        invenUomNo: rawProdData.invenUomNo,
        prodUomNo: rawProdData.prodUomNo,
        conversionFactor: rawProdData.conversionFactor,
        buyLocal: rawProdData.buyLocal,
        accountCode: rawProdData.accountCode,
        newAccountCode: rawProdData.newAccountCode,
        usageAlgorithm: rawProdData.usageAlgorithm,
        frozen: rawProdData.frozen,
        safetyPolicy: rawProdData.safetyPolicy,
        glAccountingCode: rawProdData.glAccountingCode,
        glAccountCategoryDescription: rawProdData.glAccountCategoryDescription,
      });
    }
  }, [rawProdData]);

  const [formValues, setFormValues] = useState<RawProdFormValues>({
    mxRef: isAddNew ? 'NATION' : '',
    moaRawProdNo: undefined,
    rawProdNo: undefined,
    rawProdTpCode: '',
    description: '',
    shortDescription: '',
    statusNo: undefined,
    classNo: undefined,
    printSeq: undefined,
    pcaNo: undefined,
    occasionNo: undefined,
    invenUomNo: undefined,
    prodUomNo: undefined,
    conversionFactor: undefined,
    buyLocal: undefined,
    accountCode: '',
    newAccountCode: '',
    usageAlgorithm: '',
    frozen: undefined,
    safetyPolicy: undefined,
    glAccountingCode: '',
    glAccountCategoryDescription: '',
  });

  const [formErrors, setFormErrors] = useState<RawProdFormErrors>({
    mxRef: '',
    moaRawProdNo: '',
    rawProdNo: '',
    rawProdTpCode: '',
    description: '',
    shortDescription: '',
    statusNo: '',
    classNo: '',
    printSeq: '',
    pcaNo: '',
    occasionNo: '',
    invenUomNo: '',
    prodUomNo: '',
    conversionFactor: '',
    buyLocal: '',
    accountCode: '',
    newAccountCode: '',
    usageAlgorithm: '',
    frozen: '',
    safetyPolicy: '',
    glAccountingCode: '',
    glAccountCategoryDescription: '',
  });

  const isSubassembly = formValues.rawProdTpCode === RAW_PROD_TYPE_CODE.SubAssembly;
  const [selectedSubassemblyRawProds, setSelectedSubassemblyRawProds] = useState<SubAssembly[]>([]);

  const validateFormValues = () => {
    let error = false;
    let errorMessages: RawProdFormErrors = {
      mxRef: '',
      description: '',
      shortDescription: '',
      statusNo: '',
      classNo: '',
      printSeq: '',
      pcaNo: '',
      occasionNo: '',
      invenUomNo: '',
      prodUomNo: '',
      conversionFactor: '',
      buyLocal: '',
      newAccountCode: '',
      accountCode: '',
      usageAlgorithm: '',
      safetyPolicy: '',
    };
    if ([null, undefined, ''].includes(formValues.mxRef)) {
      errorMessages.mxRef = 'MxRef is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.rawProdTpCode)) {
      errorMessages.rawProdTpCode = 'Raw Product Type Code is required.';
      error = true;
    }
    if (
      [null, undefined, ''].includes(formValues.description) ||
      formValues.description.length < 1 ||
      formValues.description.length > 50
    ) {
      errorMessages.description = 'Description must be 1-50 characters.';
      error = true;
    }
    if (
      [null, undefined, ''].includes(formValues.shortDescription) ||
      formValues.shortDescription.length < 1 ||
      formValues.shortDescription.length > 16
    ) {
      errorMessages.shortDescription = 'Short Description must be 1-16 characters.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.statusNo)) {
      errorMessages.statusNo = 'Status is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.classNo)) {
      errorMessages.classNo = 'Class is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.printSeq)) {
      errorMessages.printSeq = 'Print seq is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.pcaNo)) {
      errorMessages.pcaNo = 'PCA Number is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.occasionNo)) {
      errorMessages.occasionNo = 'Occasion is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.invenUomNo)) {
      errorMessages.invenUomNo = 'Inventory UOM is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.prodUomNo)) {
      errorMessages.prodUomNo = 'Prod UOM is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.conversionFactor)) {
      errorMessages.conversionFactor = 'Conversion Factor is required.';
      error = true;
    }
    if (formValues.buyLocal === undefined) {
      errorMessages.buyLocal = 'Buy Local is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.newAccountCode)) {
      errorMessages.newAccountCode = 'New Account Code is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.accountCode)) {
      errorMessages.accountCode = 'Account Code is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.usageAlgorithm)) {
      errorMessages.usageAlgorithm = 'Usage Algorithm is required.';
      error = true;
    }
    if (formValues.frozen === undefined) {
      errorMessages.frozen = 'Frozen is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.safetyPolicy)) {
      errorMessages.safetyPolicy = 'Safety is required.';
      error = true;
    }

    setFormErrors((formErrors) => {
      return { ...formErrors, ...errorMessages };
    });
    return error;
  };

  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    const isError = validateFormValues();
    if (isSubassembly && selectedSubassemblyRawProds.length === 0) {
      showToast({
        label: 'Subassembly requires at least one subassembly raw product to be assigned.',
        subtext: 'Please add a subassembly raw product record or change the type code.',
        id: 'noSubassemblyRawProds',
        toastType: ToastTypes.Error,
      });
      return;
    }
    if (isError) return;
    mutate(
      {
        id: formValues.id as number,
        mxRef: formValues.mxRef,
        moaRawProdNo: formValues.moaRawProdNo,
        rawProdNo: formValues.rawProdNo,
        rawProdTpCode: formValues.rawProdTpCode,
        description: formValues?.description,
        shortDescription: formValues?.shortDescription,
        statusNo: formValues?.statusNo as number,
        classNo: formValues?.classNo as number,
        printSeq: formValues?.printSeq as number,
        pcaNo: formValues?.pcaNo as number,
        occasionNo: formValues?.occasionNo as number,
        invenUomNo: formValues?.invenUomNo as number,
        prodUomNo: formValues?.prodUomNo as number,
        conversionFactor: formValues?.conversionFactor as number,
        buyLocal: formValues?.buyLocal,
        accountCode: formValues?.accountCode,
        newAccountCode: formValues?.newAccountCode,
        usageAlgorithm: formValues?.usageAlgorithm,
        frozen: formValues?.frozen,
        safetyPolicy: formValues?.safetyPolicy as number,
        glAccountingCode: formValues.glAccountingCode,
        glAccountCategoryDescription: formValues.glAccountCategoryDescription,
        subAssemblies: selectedSubassemblyRawProds,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('rawprodsource');
          navigateWithToast('/tables/rawproducts', {
            label: 'New raw product has been added successfully.',
            id: 'rawProdAddSuccess',
          });
        },
        onError: (error) => {
          if (error || addRawProdError || addRawProdIsError) {
            showToast(genericErrorShowToastOptions);
          }
        },
      }
    );
  };
  if (isSubassembly && rawProdData?.mxRef === 'NATION') {
    return <Navigate to='/tables/rawproducts' />;
  }
  if (isAddNew)
    return (
      <RawProdForm
        flow={RawProdFlows.addNew}
        formValues={formValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setFormValues={setFormValues}
        headerTitle={'Add New Raw Product'}
        selectedSubassemblyRawProds={selectedSubassemblyRawProds}
        setSelectedSubassemblyRawProds={setSelectedSubassemblyRawProds}
        saveDisabled={false}
        handleSave={handleSave}
        isButtonLoading={isLoading}
        disabledInputs={['mxRef', 'mmsRawProdNo', 'rawProdNo', 'updatedBy', 'updatedDate']}
      />
    );
  if (rawProdData?.mxRef === 'NATION')
    return (
      <RawProdForm
        flow={RawProdFlows.addLikeNation}
        formValues={formValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setFormValues={setFormValues}
        selectedSubassemblyRawProds={selectedSubassemblyRawProds}
        setSelectedSubassemblyRawProds={setSelectedSubassemblyRawProds}
        headerTitle={'Add Like Nation'}
        saveDisabled={false}
        handleSave={handleSave}
        isButtonLoading={isLoading}
        disabledInputs={['mmsRawProdNo', 'rawProdNo', 'rawProdTpCode', 'updatedBy', 'updatedDate']}
      />
    );
  if (rawProdData !== undefined && rawProdData?.mxRef !== 'NATION')
    return <AddRawProductOverrides selectedRawProdId={id} />;
  return <></>;
};

export default AddRawProd;
